import React, { useState } from 'react'
import { House, Plus, Users, User, Prohibit, IconProps } from '@phosphor-icons/react'
import {
  advagymGreen,
  gymGridColorCss,
  lightGrey,
  mediumGrey,
  partnerGridColorCss,
  suColorCss,
} from 'app/protected/roles/util/colors'
import i18next from 'i18next'
import Button from 'app/protected/common/components/Button'

// Maps the legacy Semantic UI icon-font names that consumers still pass to
// Phosphor icon components (new UI). Unmapped names render nothing.
const ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  home: House,
  plus: Plus,
  users: Users,
  user: User,
  ban: Prohibit,
}

export type BadgeType =
  | 'su'
  | 'partner'
  | 'gym'
  | 'coach'
  | 'puck'
  | 'basic'
  | 'error'
  | 'header'

interface BadgeProps {
  label?: string
  icon?: string
  customIcon?: string
  imageUrl?: string
  type?: BadgeType
  size?: 'big' | 'medium' | 'small' | 'tiny'
  square?: boolean
  disabled?: boolean
  preview?: boolean
  badgeColor?: string
}

export function getBadge(badgeLabel: string) {
  const badgeLength = badgeLabel.length
  if (badgeLength == 3) {
    if (
      badgeLabel[0].match(/^[A-Z]+$/) &&
      badgeLabel[1] >= '0' &&
      badgeLabel[1] <= '9'
    ) {
      if (badgeLabel[1] == '1' || badgeLabel[1] == '2') {
        return badgeLabel[0] + badgeLabel[1] + badgeLabel[2]
      }
    }
  }
  if (badgeLength > 2) {
    const tempLetters = badgeLabel.match(/\b(\w)/g)
    if (tempLetters) {
      if (tempLetters[1]) {
        return tempLetters[0] + tempLetters[1]
      }
      return tempLetters[0]
    } else {
      const specialCharLength = badgeLabel.length
      if (specialCharLength > 2) {
        return badgeLabel[0] + badgeLabel[1]
      } else if (specialCharLength === 1) {
        return badgeLabel[0]
      }
      return 'NN'
    }
  } else {
    if (badgeLength == 1) {
      return badgeLabel[0]
    }
    const tempTwoLetter = badgeLabel[0] + badgeLabel[1]
    return tempTwoLetter
  }
}

function Badge(props: BadgeProps) {
  const initialState = { modalOpen: false }
  const [{ modalOpen }, setState] = useState(initialState)
  const toggleModal = () => {
    setState(prevState => ({ ...prevState, modalOpen: !prevState.modalOpen }))
  }

  const { label, icon, customIcon, imageUrl, type, size, square, disabled, preview, badgeColor } = props
  const isPuck = type === 'puck'
  const iconIsSet = icon ? icon.length > 0 : false
  const customIconIsSet = customIcon
  const imageUrlIsSet = imageUrl ? imageUrl.length > 0 : false
  const isPng = imageUrl ? imageUrl.slice(-4) === '.png' : false

  const labelIsSet = label ? label.trim().length > 0 : false

  let badgeLabel = 'NN'
  if (labelIsSet) {
    badgeLabel = label
  }

  const color = () => {
    const coachHeaderBadge = 'var(--color-brand-green)'
    switch (type) {
      case 'su':
        return { badge: suColorCss, icon: 'white' }
      case 'partner':
        return { badge: partnerGridColorCss, icon: 'white' }
      case 'gym':
        return { badge: gymGridColorCss, icon: 'white' }
      case 'coach':
        return { badge: coachHeaderBadge, icon: 'white' }
      case 'puck':
        return { badge: 'black', icon: 'white' }
      case 'header':
        return { badge: coachHeaderBadge, icon: 'white' }
      case 'error':
        return { badge: 'red', icon: 'white' }
      case 'basic':
        return { badge: lightGrey, icon: mediumGrey }

      default:
        return { badge: lightGrey, icon: mediumGrey }
    }
  }

  const sizeOf = () => {
    switch (size) {
      case 'big':
        return { circleSize: '70px', fontSize: '32px' }
      case 'medium':
        return { circleSize: '50px', fontSize: '23px' }
      case 'small':
        return { circleSize: '35px', fontSize: '14px' }
      case 'tiny':
        return { circleSize: '20px', fontSize: '10px' }
      default:
        return { circleSize: '35px', fontSize: '14px' }
    }
  }

  let setCounts = 0
  if (iconIsSet) setCounts += 1
  if (customIconIsSet) setCounts += 1
  if (imageUrlIsSet) setCounts += 1

  if (setCounts > 1)
    throw Error('just on of icon, customIcon and image should be set')

  const basicBadgeProps: React.CSSProperties = {
    opacity: disabled ? 0.7 : null,
    display: 'flex',
    maxWidth: sizeOf().circleSize,
    maxHeight: sizeOf().circleSize,
    minWidth: sizeOf().circleSize,
    minHeight: sizeOf().circleSize,
    width: sizeOf().circleSize,
    height: sizeOf().circleSize,
    backgroundColor: isPng ? 'white' : (badgeColor || color().badge),
    border: `${!imageUrlIsSet && '1px'} solid ${badgeColor || color().badge}`,
    boxSizing: 'border-box',
    borderRadius: square ? '25%' : '50%',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    textAlign: 'center',
  }
  const hasPreview = imageUrlIsSet && preview

  const IconComponent = iconIsSet ? ICON_MAP[icon] : undefined

  const modalPreview = () => {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={toggleModal}
      >
        <div
          className="bg-white rounded-default p-4 max-w-sm w-full mx-4 font-nunito"
          onClick={e => e.stopPropagation()}
        >
          <h3 className="font-bold text-base text-brand-black mb-3">
            {i18next.t('picture')}
          </h3>
          <img src={imageUrl} className="block w-full rounded" alt="" />
          <div className="flex justify-center mt-3">
            <Button variant="primary" onClick={toggleModal}>
              {i18next.t('close')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {imageUrlIsSet && !isPng ? (
        <div
          style={{
            ...basicBadgeProps,
            background: `url(${imageUrl}) no-repeat center center`,
            backgroundSize: 'cover',
            cursor: hasPreview ? 'pointer' : null,
          }}
          onClick={hasPreview ? () => toggleModal() : null}
        />
      ) : (
        <div
          style={{
            ...basicBadgeProps,
            borderWidth: isPuck ? '4px' : '',
            borderStyle: isPuck ? 'solid' : '',
            borderColor: isPuck ? advagymGreen : '',
          }}
        >
          {iconIsSet && IconComponent && (
            <IconComponent
              size={parseInt(sizeOf().fontSize, 10)}
              color={color().icon}
              weight="bold"
            />
          )}

          {customIconIsSet && (
            <img
              src={customIcon}
              className="inline-block p-[5px]"
              alt=""
            />
          )}

          {imageUrlIsSet && (
            <img
              src={imageUrl}
              className="rounded-full"
              style={{ width: sizeOf().circleSize, height: sizeOf().circleSize }}
              alt=""
            />
          )}

          {!iconIsSet && !imageUrlIsSet && !customIconIsSet && (
            <span
              style={{
                fontSize: sizeOf().fontSize,
                color: type ? 'white' : 'black',
                textTransform: 'uppercase',
              }}
            >
              {getBadge(badgeLabel)}
            </span>
          )}
        </div>
      )}
      {hasPreview && modalOpen && modalPreview()}
    </>
  )
}

export default Badge
